import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useBoundStore } from "../../store";
import { useI18n } from "../../i18n";

interface CreatePlaybookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreatePlaybookDialog({ open, onOpenChange }: CreatePlaybookDialogProps) {
  const { t } = useI18n();
  const createPlaybook = useBoundStore((state) => state.createPlaybook);
  const isLoading = useBoundStore((state) => state.isLoading);

  const [name, setName] = useState("");
  const [agreementType, setAgreementType] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !agreementType.trim()) {
      toast.error(t.playbooks.createDialog.requiredFields);
      return;
    }

    const playbook = await createPlaybook({
      name: name.trim(),
      agreementType: agreementType.trim(),
      isPrivate,
    });

    if (playbook) {
      toast.success(t.playbooks.createDialog.createSuccess);
      // Reset form
      setName("");
      setAgreementType("");
      setIsPrivate(true);
      onOpenChange(false);
    } else {
      // Pull the latest error the store wrote (e.g. trial cap hit) so the
      // user sees the specific reason instead of a generic fallback.
      const error = useBoundStore.getState().error;
      toast.error(error || t.playbooks.createDialog.createFailed);
    }
  };

  const handleCancel = () => {
    setName("");
    setAgreementType("");
    setIsPrivate(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t.playbooks.createDialog.title}</DialogTitle>
            <DialogDescription>
              {t.playbooks.createDialog.description}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                {t.playbooks.createDialog.nameLabel} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.playbooks.createDialog.namePlaceholder}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="agreementType">
                {t.playbooks.createDialog.agreementTypeLabel} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="agreementType"
                value={agreementType}
                onChange={(e) => setAgreementType(e.target.value)}
                placeholder={t.playbooks.createDialog.agreementTypePlaceholder}
                required
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="isPrivate">{t.playbooks.createDialog.privateLabel}</Label>
                <p className="text-sm text-muted-foreground">
                  {isPrivate
                    ? t.playbooks.createDialog.privateOnlyYou
                    : t.playbooks.createDialog.privateEveryone}
                </p>
              </div>
              <Switch id="isPrivate" checked={isPrivate} onCheckedChange={setIsPrivate} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
              {t.playbooks.createDialog.cancel}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t.playbooks.createDialog.creating : t.playbooks.createDialog.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
